import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flame, Handshake } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LightRays } from '@/components/ui/light-rays';
import { SplitText } from '@/components/ui/split-text';
import { BackgroundPaths } from '@/components/ui/background-paths';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const INJECTED_STYLES = `
  .gsap-reveal { visibility: hidden; }

  /* Environment: ThinkinCode homepage, white canvas, subtle texture */
  .film-grain {
      position: absolute; inset: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 50; opacity: 0.03; mix-blend-mode: multiply;
      background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><filter id="noiseFilter"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23noiseFilter)"/></svg>');
  }

  .bg-grid-theme {
      background-size: 72px 72px;
      background-image: 
          linear-gradient(to right, rgba(0, 0, 0, 0.04) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(0, 0, 0, 0.04) 1px, transparent 1px);
      mask-image: radial-gradient(ellipse at center, black 0%, transparent 72%);
      -webkit-mask-image: radial-gradient(ellipse at center, black 0%, transparent 72%);
  }

  /* -------------------------------------------------------------------
     PHYSICAL SKEUOMORPHIC MATERIALS (Restored 3D Depth)
  ---------------------------------------------------------------------- */
  
  /* Hero type: matches homepage .hero-title / Apple-inspired black & Inter */
  .text-3d-matte {
      color: #000000;
      font-weight: 700;
      letter-spacing: -0.02em;
      line-height: 1.1;
      text-shadow: 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .text-silver-matte {
      color: #000000;
      font-weight: 800;
      letter-spacing: -0.035em;
      line-height: 1.1;
      background: linear-gradient(180deg, #000000 0%, #4b5563 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      transform: translateZ(0);
      filter: drop-shadow(0 2px 14px rgba(0, 0, 0, 0.06));
  }

  /* Brand wordmark on white card: dark, minimal */
  .text-card-silver-matte {
      color: #000000;
      background: linear-gradient(180deg, #000000 0%, #374151 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      transform: translateZ(0);
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.04));
  }

  /* Card: clean white panel (homepage-aligned) */
  .premium-depth-card {
      background: #ffffff;
      box-shadow:
          0 32px 64px -28px rgba(0, 0, 0, 0.1),
          0 12px 24px -12px rgba(0, 0, 0, 0.06),
         0 0 0 1px rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(0, 0, 0, 0.06);
      position: relative;
  }

  .card-sheen {
      position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 50;
      background: radial-gradient(720px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0,0,0,0.04) 0%, transparent 45%);
      mix-blend-mode: multiply;
      opacity: 0.65;
      transition: opacity 0.3s ease;
  }

  /* Realistic iPhone Mockup Hardware */
  .iphone-bezel {
      background-color: #111;
      box-shadow: 
          inset 0 0 0 2px #52525B, 
          inset 0 0 0 7px #000, 
          0 40px 80px -15px rgba(0,0,0,0.9),
          0 15px 25px -5px rgba(0,0,0,0.7);
      transform-style: preserve-3d;
  }

  .hardware-btn {
      background: linear-gradient(90deg, #404040 0%, #171717 100%);
      box-shadow: 
          -2px 0 5px rgba(0,0,0,0.8),
          inset -1px 0 1px rgba(255,255,255,0.15),
          inset 1px 0 2px rgba(0,0,0,0.8);
      border-left: 1px solid rgba(255,255,255,0.05);
  }
  
  .screen-glare {
      background: linear-gradient(110deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 45%);
  }

  .widget-depth {
      background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%);
      box-shadow: 
          0 10px 20px rgba(0,0,0,0.3),
          inset 0 1px 1px rgba(255,255,255,0.05),
          inset 0 -1px 1px rgba(0,0,0,0.5);
      border: 1px solid rgba(255,255,255,0.03);
  }

  .floating-ui-badge {
      background: rgba(255, 255, 255, 0.94);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      box-shadow:
          0 0 0 1px rgba(0, 0, 0, 0.06),
          0 12px 32px -12px rgba(0, 0, 0, 0.1);
  }

  /* CTAs: same language as homepage .hero-cta */
  .landing-cta-group {
      display: flex;
      gap: 1.5rem;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
  }
  .landing-cta {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 1rem 2.5rem;
      text-decoration: none;
      font-weight: 600;
      border-radius: 9999px;
      transition: all 0.3s ease;
      font-size: 1rem;
      font-family: var(--font-primary, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif);
  }
  .landing-cta-primary {
      background: #000000;
      color: #ffffff;
  }
  .landing-cta-primary:hover {
      background: #1f2937;
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  .landing-cta-secondary {
      background: transparent;
      color: #000000;
      border: 1px solid #d1d5db;
  }
  .landing-cta-secondary:hover {
      background: #f9fafb;
      border-color: #9ca3af;
      transform: translateY(-2px);
  }
  @media (max-width: 640px) {
    .landing-cta-group { flex-direction: column; width: 100%; max-width: 22rem; }
    .landing-cta { width: 100%; text-align: center; }
  }

  .progress-ring {
      transform: rotate(-90deg);
      transform-origin: center;
      stroke-dasharray: 402;
      stroke-dashoffset: 402;
      stroke-linecap: round;
  }
`;

export interface CinematicHeroProps extends React.HTMLAttributes<HTMLDivElement> {
  brandName?: string;
  /** First beat: main headline (replaces legacy two-line taglines). */
  openingTitle?: string;
  /** First beat: supporting paragraph under the headline. */
  openingBody?: React.ReactNode;
  cardHeading?: string;
  cardDescription?: React.ReactNode;
  metricValue?: number;
  metricLabel?: string;
}

export function CinematicHero({
  brandName,
  openingTitle = 'Systems. Platforms. Digital Infrastructure.',
  openingBody = (
    <>
      ThinkinCode builds scalable digital systems that improve operations, increase efficiency, and drive measurable
      business outcomes. We transform complex business challenges into elegant, high-performance solutions that scale
      with your growth.
    </>
  ),
  cardHeading = 'Where ideas meet code.',
  cardDescription = (
    <>
      We don&apos;t just ship software. We craft calm, precise experiences and systems built to scale with your
      business.
    </>
  ),
  metricValue = 100,
  metricLabel = 'Projects delivered',
  className,
  ...props
}: CinematicHeroProps) {
  const showBrandWordmark = Boolean(brandName?.trim());
  const containerRef = useRef<HTMLDivElement>(null);
  const mainCardRef = useRef<HTMLDivElement>(null);
  const mockupRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number>(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (window.scrollY > window.innerHeight * 2) return;

      cancelAnimationFrame(requestRef.current);

      requestRef.current = requestAnimationFrame(() => {
        if (mainCardRef.current && mockupRef.current) {
          const rect = mainCardRef.current.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;

          mainCardRef.current.style.setProperty('--mouse-x', `${mouseX}px`);
          mainCardRef.current.style.setProperty('--mouse-y', `${mouseY}px`);

          const xVal = (e.clientX / window.innerWidth - 0.5) * 2;
          const yVal = (e.clientY / window.innerHeight - 0.5) * 2;

          gsap.to(mockupRef.current, {
            rotationY: xVal * 12,
            rotationX: -yVal * 12,
            ease: 'power3.out',
            duration: 1.2,
          });
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(requestRef.current);
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      gsap.set('.split-text-char', { autoAlpha: 0, y: 44, filter: 'blur(14px)', rotationX: -18 });
      gsap.set('.landing-hero-lead', { autoAlpha: 0, y: 40, filter: 'blur(12px)' });
      gsap.set('.main-card', { y: window.innerHeight + 200, autoAlpha: 1 });
      gsap.set(['.card-left-text', '.card-right-text', '.mockup-scroll-wrapper', '.floating-badge', '.phone-widget'], {
        autoAlpha: 0,
      });
      gsap.set('.landing-works-opening', { autoAlpha: 0, y: 48, pointerEvents: 'none' });
      gsap.set('.landing-works-opening .works-usecase-heading', { autoAlpha: 0, y: 24 });
      gsap.set('.landing-works-opening .works-usecase-title', { autoAlpha: 0, y: 32 });
      gsap.set('.landing-works-opening .animated-card', { autoAlpha: 0, y: 44 });

      const introTl = gsap.timeline({ delay: 0.3 });
      introTl
        .to('.split-text-char', {
          duration: 1.05,
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          rotationX: 0,
          ease: 'power3.out',
          stagger: 0.018,
        })
        .to(
          '.landing-hero-lead',
          {
            duration: 1.2,
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            ease: 'power3.out',
          },
          '-=0.85',
        );

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          // Mobile: shorten the pinned scroll distance so the handoff feels snappier.
          end: isMobile ? '+=2600' : '+=5600',
          pin: true,
          scrub: isMobile ? 0.45 : 0.85,
          anticipatePin: 1,
          fastScrollEnd: true,
        },
      });

      scrollTl
        .to(
          ['.hero-text-wrapper', '.bg-grid-theme'],
          { scale: 1.15, filter: 'blur(20px)', opacity: 0.2, ease: 'power2.inOut', duration: 2 },
          0,
        )
        .to('.main-card', { y: 0, ease: 'power3.inOut', duration: 2 }, 0)
        .to('.main-card', { width: '100%', height: '100%', borderRadius: '0px', ease: 'power3.inOut', duration: 1.5 })
        .fromTo(
          '.mockup-scroll-wrapper',
          { y: 300, z: -500, rotationX: 50, rotationY: -30, autoAlpha: 0, scale: 0.6 },
          {
            y: 0,
            z: 0,
            rotationX: 0,
            rotationY: 0,
            autoAlpha: 1,
            scale: 1,
            ease: 'expo.out',
            duration: 2.5,
          },
          '-=0.8',
        )
        .fromTo(
          '.phone-widget',
          { y: 40, autoAlpha: 0, scale: 0.95 },
          { y: 0, autoAlpha: 1, scale: 1, stagger: 0.15, ease: 'back.out(1.2)', duration: 1.5 },
          '-=1.5',
        )
        .to('.progress-ring', { strokeDashoffset: 60, duration: 2, ease: 'power3.inOut' }, '-=1.2')
        .to(
          '.counter-val',
          { innerHTML: metricValue, snap: { innerHTML: 1 }, duration: 2, ease: 'expo.out' },
          '-=2.0',
        )
        .fromTo(
          '.floating-badge',
          { y: 100, autoAlpha: 0, scale: 0.7, rotationZ: -10 },
          {
            y: 0,
            autoAlpha: 1,
            scale: 1,
            rotationZ: 0,
            ease: 'back.out(1.5)',
            duration: 1.5,
            stagger: 0.2,
          },
          '-=2.0',
        )
        .fromTo(
          '.card-left-text',
          { x: -50, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, ease: 'power4.out', duration: 1.5 },
          '-=1.5',
        );

      if (showBrandWordmark) {
        scrollTl.fromTo(
          '.card-right-text',
          { x: 50, autoAlpha: 0, scale: 0.8 },
          { x: 0, autoAlpha: 1, scale: 1, ease: 'expo.out', duration: 1.5 },
          '<',
        );
      }

      const worksHandoffFadeTargets = ['.mockup-scroll-wrapper', '.floating-badge', '.card-left-text'];
      if (showBrandWordmark) worksHandoffFadeTargets.push('.card-right-text');

      scrollTl
        .to({}, { duration: isMobile ? 0.55 : 1.15 })
        .set('.hero-text-wrapper', { autoAlpha: 0 })
        .to(
          worksHandoffFadeTargets,
          {
            scale: 0.92,
            y: -36,
            z: -180,
            autoAlpha: 0,
            ease: 'power3.in',
            duration: isMobile ? 0.7 : 1.1,
            stagger: 0.04,
          },
          'worksHandoff',
        )
        .to(
          '.main-card',
          {
            autoAlpha: 0,
            scale: 0.94,
            filter: 'blur(12px)',
            ease: 'power3.inOut',
            duration: isMobile ? 0.75 : 1.35,
          },
          'worksHandoff+=0.15',
        )
        .to(
          '.landing-works-opening',
          {
            autoAlpha: 1,
            y: 0,
            pointerEvents: 'auto',
            ease: 'power3.out',
            duration: isMobile ? 0.7 : 1.1,
          },
          'worksHandoff+=0.35',
        )
        .to(
          '.landing-works-opening .works-usecase-heading',
          { autoAlpha: 1, y: 0, ease: 'power3.out', duration: isMobile ? 0.4 : 0.65 },
          'worksHandoff+=0.5',
        )
        .to(
          '.landing-works-opening .works-usecase-title',
          { autoAlpha: 1, y: 0, ease: 'power3.out', duration: isMobile ? 0.5 : 0.8 },
          'worksHandoff+=0.62',
        )
        .to(
          '.landing-works-opening .animated-card',
          {
            autoAlpha: 1,
            y: 0,
            ease: 'power3.out',
            duration: isMobile ? 0.55 : 0.85,
            stagger: 0.07,
          },
          'worksHandoff+=0.78',
        )
        .to({}, { duration: isMobile ? 0.6 : 1.1 });
    }, containerRef);

    return () => ctx.revert();
  }, [metricValue, showBrandWordmark]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative box-border flex min-h-[100svh] w-screen items-center justify-center overflow-hidden bg-white pt-[4.25rem] font-sans text-foreground antialiased md:pt-24',
        className,
      )}
      style={{ perspective: '1500px' }}
      {...props}
    >
      <style dangerouslySetInnerHTML={{ __html: INJECTED_STYLES }} />
      <LightRays
        className="z-0 opacity-90"
        count={14}
        speed={12}
        blur={46}
        length="92vh"
        color="rgba(95, 175, 255, 0.28)"
      />
      <div className="film-grain" aria-hidden="true" />
      <div className="bg-grid-theme pointer-events-none absolute inset-0 z-0 opacity-[0.4]" aria-hidden="true" />

      <div className="hero-text-wrapper absolute z-10 flex w-screen max-w-5xl flex-col items-center justify-center px-4 text-center [transform-style:preserve-3d] will-change-transform md:px-8">
        <h1 className="mb-5 max-w-4xl text-balance text-[clamp(2.25rem,5.2vw,4.5rem)] font-bold tracking-tighter text-neutral-950 md:mb-6">
          <SplitText text={openingTitle} />
        </h1>
        <p className="landing-hero-lead gsap-reveal max-w-2xl text-[0.95rem] font-normal leading-relaxed text-neutral-600 md:text-[1.0625rem] md:leading-[1.65]">
          {openingBody}
        </p>
      </div>

      <div
        className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
        style={{ perspective: '1500px' }}
      >
        <div
          ref={mainCardRef}
          className="main-card premium-depth-card gsap-reveal pointer-events-auto relative flex h-[92vh] w-[92vw] items-center justify-center overflow-hidden rounded-[32px] md:h-[85vh] md:w-[85vw] md:rounded-[40px]"
        >
          <BackgroundPaths className="z-[1] opacity-80" opacity={0.85} />
          <div className="card-sheen" aria-hidden="true" />

          <div
            className={cn(
              'relative z-10 mx-auto flex h-full w-full flex-col items-center justify-evenly px-4 py-6 lg:grid lg:items-center lg:gap-8 lg:px-12 lg:py-0',
              showBrandWordmark ? 'max-w-7xl lg:grid-cols-3' : 'max-w-5xl lg:grid-cols-2',
            )}
          >
            {showBrandWordmark ? (
              <div className="card-right-text gsap-reveal order-1 z-20 flex w-full justify-center lg:order-3 lg:justify-end">
                <h2 className="text-card-silver-matte text-6xl font-black uppercase tracking-tighter md:text-[6rem] lg:mt-0 lg:text-[8rem]">
                  {brandName}
                </h2>
              </div>
            ) : null}

            <div
              className="mockup-scroll-wrapper order-2 relative z-10 flex h-[380px] w-full items-center justify-center lg:order-2 lg:h-[600px]"
              style={{ perspective: '1000px' }}
            >
              <div className="relative flex h-full w-full scale-[0.65] transform items-center justify-center md:scale-[0.85] lg:scale-100">
                <div
                  ref={mockupRef}
                  className="iphone-bezel relative flex h-[580px] w-[280px] flex-col rounded-[3rem] [transform-style:preserve-3d] will-change-transform"
                >
                  <div className="hardware-btn absolute left-[-3px] top-[120px] z-0 h-[25px] w-[3px] rounded-l-md" aria-hidden="true" />
                  <div className="hardware-btn absolute left-[-3px] top-[160px] z-0 h-[45px] w-[3px] rounded-l-md" aria-hidden="true" />
                  <div className="hardware-btn absolute left-[-3px] top-[220px] z-0 h-[45px] w-[3px] rounded-l-md" aria-hidden="true" />
                  <div
                    className="hardware-btn absolute right-[-3px] top-[170px] z-0 h-[70px] w-[3px] scale-x-[-1] rounded-r-md"
                    aria-hidden="true"
                  />

                  <div className="absolute inset-[7px] z-10 overflow-hidden rounded-[2.5rem] bg-[#050914] text-white shadow-[inset_0_0_15px_rgba(0,0,0,1)]">
                    <div className="screen-glare pointer-events-none absolute inset-0 z-40" aria-hidden="true" />

                    <div className="absolute left-1/2 top-[5px] z-50 flex h-[28px] w-[100px] -translate-x-1/2 items-center justify-end rounded-full bg-black px-3 shadow-[inset_0_-1px_2px_rgba(255,255,255,0.1)]">
                      <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
                    </div>

                    <div className="relative flex h-full w-full flex-col px-5 pb-8 pt-12">
                      <div className="phone-widget mb-8 flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Today</span>
                          <span className="text-xl font-bold tracking-tight text-white drop-shadow-md">Journey</span>
                        </div>
                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-sm font-bold text-neutral-200 shadow-lg shadow-black/50">
                          JS
                        </div>
                      </div>

                      <div className="phone-widget relative mx-auto mb-8 flex h-44 w-44 items-center justify-center drop-shadow-[0_15px_25px_rgba(0,0,0,0.8)]">
                        <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
                          <circle cx="88" cy="88" r="64" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="12" />
                          <circle
                            className="progress-ring"
                            cx="88"
                            cy="88"
                            r="64"
                            fill="none"
                            stroke="#f3f4f6"
                            strokeWidth="12"
                          />
                        </svg>
                        <div className="z-10 flex flex-col items-center text-center">
                          <span className="counter-val text-4xl font-extrabold tracking-tighter text-white">0</span>
                          <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-neutral-400">
                            {metricLabel}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="phone-widget widget-depth flex items-center rounded-2xl p-3">
                          <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-gradient-to-br from-white/12 to-white/5 shadow-inner">
                            <svg
                              className="h-4 w-4 text-neutral-200 drop-shadow-md"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="mb-2 h-2 w-20 rounded-full bg-neutral-300 shadow-inner" />
                            <div className="h-1.5 w-12 rounded-full bg-neutral-600 shadow-inner" />
                          </div>
                        </div>
                        <div className="phone-widget widget-depth flex items-center rounded-2xl p-3">
                          <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-xl border border-white/12 bg-gradient-to-br from-white/10 to-white/4 shadow-inner">
                            <svg
                              className="h-4 w-4 text-neutral-300 drop-shadow-md"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="mb-2 h-2 w-16 rounded-full bg-neutral-300 shadow-inner" />
                            <div className="h-1.5 w-24 rounded-full bg-neutral-600 shadow-inner" />
                          </div>
                        </div>
                      </div>

                      <div className="absolute bottom-2 left-1/2 h-[4px] w-[120px] -translate-x-1/2 rounded-full bg-white/20 shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
                    </div>
                  </div>
                </div>

                <div className="floating-badge floating-ui-badge absolute left-[-15px] top-6 z-30 flex items-center gap-3 rounded-xl p-3 lg:left-[-80px] lg:top-12 lg:gap-4 lg:rounded-2xl lg:p-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 lg:h-10 lg:w-10">
                    <Flame className="h-4 w-4 text-neutral-700 lg:h-5 lg:w-5" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-tight text-neutral-900 lg:text-sm">Ship milestone</p>
                    <p className="text-[10px] font-medium text-neutral-500 lg:text-xs">On schedule</p>
                  </div>
                </div>

                <div className="floating-badge floating-ui-badge absolute bottom-12 right-[-15px] z-30 flex items-center gap-3 rounded-xl p-3 lg:bottom-20 lg:right-[-80px] lg:gap-4 lg:rounded-2xl lg:p-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 lg:h-10 lg:w-10">
                    <Handshake className="h-4 w-4 text-neutral-700 lg:h-5 lg:w-5" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-tight text-neutral-900 lg:text-sm">Client sync</p>
                    <p className="text-[10px] font-medium text-neutral-500 lg:text-xs">Delivered</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-left-text gsap-reveal order-3 z-20 flex w-full flex-col justify-center px-4 text-center lg:order-1 lg:max-w-none lg:px-0 lg:text-left">
              <h3 className="mb-3 text-2xl font-bold tracking-tight text-neutral-900 md:mb-5 md:text-3xl lg:text-4xl">
                {cardHeading}
              </h3>
              <p className="mx-auto max-w-md text-sm font-normal leading-relaxed text-neutral-600 md:mx-0 md:text-base lg:max-w-none lg:text-lg">
                {cardDescription}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="landing-works-opening gsap-reveal pointer-events-none absolute inset-0 z-[30] flex flex-col items-center overflow-x-hidden overflow-y-auto px-[var(--spacing-md)] pb-12 pt-20 [transform-style:preserve-3d] md:pb-20 md:pt-24">
        <div id="works-landing-what-we-build" className="works-container w-full" />
      </div>
    </div>
  );
}
